import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";
import { fallbackColor, imagesBaseUrl } from "../../constants";
import { cardStyles } from "../../styles/card";
import { nugetIdToUrlAlias } from "../../functions";
import { box } from "../../svg";
import type { Package } from "../package";
import { mcp } from "../../svg/package";

@customElement("umb-market-package-card")
export class UmbMarketPackageCard extends LitElement {
  readonly IMG_SIZE = 64;

  @property({ type: Object })
  package!: Package;

  @property({ type: String })
  packageId?: string;

  @property({ type: Boolean })
  deck!: boolean;

  async connectedCallback() {
    super.connectedCallback();

    let dominantColor = this.package.iconDominantColor ?? fallbackColor;
    dominantColor =
      dominantColor[0] === "#" ? dominantColor : `#${dominantColor}`;

    this.style.setProperty("--dominant-color-opaque", `${dominantColor}33`);
  }

  // Insert zero-width space before each capital letter and after each dot
  // to ensure ugly package names wrap elegantly
  #insertBreaks(str: string): string {
    return str
      .replace(/([A-Z])/g, "\u200B$1") // Before capital letters
      .replace(/\./g, ".\u200B"); // After dots
  }

  #packageCardBodyForDeck() {
    const descriptionClass = {
      long: (this.package.description ?? "").length > 250,
    };

    return html`<div class="dinkus">
        <div class="image-wrapper">${this.#packageIcon()}</div>
        <div>
          <h3>${this.#insertBreaks(this.package.title)}</h3>
        </div>
      </div>

      ${when(
        this.package.description,
        () =>
          html`<p class=${classMap(descriptionClass)}>
            ${this.package.description}
          </p>`
      )}
      ${when(
        this.package.relationDescription,
        () =>
          html`<small id="relationDescription"
            >${this.package.relationDescription}</small
          >`
      )}
      <div class="card-footer">
        <umb-market-package-version-badges
          .versionsSupported=${this.package.umbracoMajorVersionsSupported}
          .cloudCertified=${this.package.isCertifiedToWorkOnUmbracoCloud}
        ></umb-market-package-version-badges>

        <umb-market-package-owner-badge .package=${this.package}>
        </umb-market-package-owner-badge>
      </div> `;
  }

  #packageCardBodyForSingle() {
    return html`
      <div class="flex">
        <div class="dinkus">
          <div class="image-wrapper">${this.#packageIcon()}</div>
          <div>
            <h2>${this.#insertBreaks(this.package.title)}</h2>
          </div>
        </div>
      </div>
      ${when(
        this.package.description,
        () => html`<p>${this.package.description}</p>`
      )}
    `;
  }

  #packageIcon() {
    // TODO => this is temporary until the MCP icon is in git.
    if (this.package.packageId === "@umbraco-cms/mcp-dev") {
      return html`${mcp}`;
    }

    if (!this.package.iconUrl) {
      return html`${box}`;
    }

    const url = this.package.iconUrl.startsWith("http")
      ? this.package.iconUrl
      : imagesBaseUrl + this.package.iconUrl;

    return html`<umb-market-src-set-image
      src=${url}
      version=${this.package.latestVersionNumber}
    ></umb-market-src-set-image>`;
  }

  /**
   * render handles the outer element - either a button if we are in
   * a card deck context, or a div if we are viewing a single package
   */
  render() {
    return html`
      ${when(
        this.deck,
        () =>
          html`<a
            role="button"
            class="card"
            href=${`/package/${nugetIdToUrlAlias(
              this.package.packageNuGetId ?? this.package.packageId
            )}`}
          >
            <div class="card-body">${this.#packageCardBodyForDeck()}</div>
          </a>`,
        () => html`<div class="card single">
          <div class="card-body">${this.#packageCardBodyForSingle()}</div>
        </div>`
      )}
    `;
  }

  static styles = [
    cardStyles,
    css`
      @media (min-width: 700px) {
        .flex {
          display: flex;
          align-items: flex-start;
        }
      }

      .flex > div {
        flex: 1;
      }

      p:last-child {
        margin-bottom: 0;
      }

      #relationDescription {
        margin-bottom: var(--s-spacer);
        line-height: 1.5;
      }

      svg {
        width: 68px;
        height: 68px;
        --fill: var(--uui-color-default);
      }
    `,
  ];
}
